import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { CheckCircle2, KeyRound, Loader2 } from 'lucide-react';
import { ApiError, RateLimitError, auth } from '../lib/apiClient';
import { passwordPasses } from '../lib/passwordPolicy';
import { useAuth } from '../context/AuthContext';
import { AuthCard } from '../components/auth/AuthCard';
import { PasswordField } from '../components/auth/PasswordField';
import { ErrorBanner } from '../components/auth/ErrorBanner';
import { RateLimitInline, isRateLimited } from '../components/RateLimitBanner';

/**
 * Phase 7-avatar deliverable. Authenticated change-password screen — wires the existing
 * Phase 5b backend endpoint (`POST /candidates/auth/change-password`) which:
 *   - validates the current password,
 *   - enforces the password policy (server-side recheck),
 *   - rotates the password,
 *   - revokes all other sessions for the candidate.
 *
 * The current session stays valid (per backend behaviour), so the user stays signed in and
 * we land them on the home page after a brief success message.
 */
export function ChangePasswordPage() {
  const { candidate, isLoading } = useAuth();
  const navigate = useNavigate();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);

  if (isLoading) {
    return (
      <AuthCard publicTheme eyebrow="Account security" title="Loading your account…">
        <div
          className="qh-auth-public__loader flex items-center justify-center py-10"
          role="status"
        >
          <Loader2 className="h-9 w-9 animate-spin" />
          <span className="sr-only">Loading account</span>
        </div>
      </AuthCard>
    );
  }
  if (!candidate) {
    return <Navigate to="/signin?return=%2Fchange-password" replace />;
  }

  const newOk = passwordPasses(next);
  const matches = next === confirm && confirm.length > 0;
  const formReady =
    current.length > 0 && newOk && matches && !submitting && !isRateLimited(rateLimitedUntil);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formReady) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await auth.changePassword({ current_password: current, new_password: next });
      setSuccess(true);
      setCurrent('');
      setNext('');
      setConfirm('');
      window.setTimeout(() => navigate('/'), 1800);
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimitedUntil(Date.now() + err.retryAfterSeconds * 1000);
      } else if (err instanceof ApiError) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Could not change your password. Try again in a moment.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <AuthCard publicTheme eyebrow="Password updated" title="Your password has been changed">
        <div className="flex flex-col items-center text-center py-2">
          <div className="qh-auth-public__status-icon qh-auth-public__status-icon--success w-20 h-20 bg-brand-accent/10 rounded-full flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-brand-accent" />
          </div>
          <p className="text-brand-muted mb-6">
            Other sessions have been signed out. Redirecting you home…
          </p>
          <Link
            to="/"
            className="block w-full py-4 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest text-center hover:bg-brand-deep transition-all"
          >
            Continue
          </Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      publicTheme
      eyebrow="Change password"
      title="Update your password"
      subtitle="Enter your current password, then choose a new one."
      footer={
        <>
          Or{' '}
          <Link to="/profile" className="text-brand-primary font-bold hover:underline">
            return to your profile
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-5">
        <ErrorBanner message={errorMessage} />
        <RateLimitInline deadlineMs={rateLimitedUntil} />

        <PasswordField
          label="Current password"
          value={current}
          onChange={setCurrent}
          autoComplete="current-password"
          placeholder="Your current password"
        />
        <PasswordField
          label="New password"
          value={next}
          onChange={setNext}
          showPolicy
          autoComplete="new-password"
          placeholder="New password"
        />
        <PasswordField
          label="Confirm new password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          placeholder="Re-enter new password"
        />
        {confirm.length > 0 && !matches && (
          <p className="text-xs font-bold text-red-700">Passwords don&apos;t match.</p>
        )}

        <button
          type="submit"
          disabled={!formReady}
          className="w-full py-4 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-brand-deep transition-all shadow-xl shadow-brand-primary/10 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <KeyRound className="w-4 h-4" />
          {submitting ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </AuthCard>
  );
}
